import React from 'react';
import { StyleSheet, ScrollView, View } from 'react-native';
import { SvgAppbackground, MainContainer, Container, Header, Tab, Button } from '@app/components';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { Permission } from '@app/common';
import { useCheckPermissions } from '@app/hooks/useCheckPermissions';

type ISubTab = {
  label: string;
  permissions?: Permission[];
  screenName: keyof ReactNavigation.RootParamList;
}

type ITab = {
  label: string;
  permissions?: Permission[];
  screenName: keyof ReactNavigation.RootParamList;
  subTabs?: undefined;
} | {
  label: string;
  permissions?: Permission[];
  screenName?: undefined;
  subTabs: ISubTab[];
}

export interface MainLayoutHeaderProps {
  tabs: ITab[];
  children?: React.ReactNode;
}

export const MainLayoutHeader = ({ tabs, children }: MainLayoutHeaderProps) => {
  const navigation = useNavigation();
  const checkPermissions = useCheckPermissions();
  const screenName = useNavigationState((state) => state?.routes[state.index]?.name)

  const goTo = (screenName: keyof ReactNavigation.RootParamList) => {
    // @ts-expect-error
    navigation.navigate(screenName);
  }

  const isTabActive = (tab: ITab) => {
    return tab.screenName === screenName || (tab?.subTabs?.some((subTab) => subTab.screenName === screenName));
  }

  const selectedTab = tabs.find(isTabActive);

  const hasSubTabs = !!selectedTab?.subTabs?.length;

  const handleTabPress = (tab: ITab) => {
    if (!tab.subTabs) {
      goTo(tab.screenName);
      return;
    }

    const subTabs = tab.subTabs.filter(subTab => checkPermissions(subTab.permissions ?? [])) ?? [];

    const firstSubTab = subTabs[0];

    if (firstSubTab) {
      goTo(firstSubTab.screenName);
    }
  }

  return (
    <Header expanded={hasSubTabs}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}>
        <Tab.Tabs>
          {tabs
            .filter(tab => {
              const subTabs = tab.subTabs ?? [];

              if (subTabs.some(subTab => !(subTab.permissions ?? []).length)) {
                return true;
              }

              const subTabsPermissions = subTabs.map(subTab => subTab.permissions ?? []).flat();

              const allPermissions = [...(tab.permissions ?? []), ...subTabsPermissions];

              return allPermissions.some(permission => checkPermissions([permission]))
            })
            .map((tab) => (
              <Tab
                key={tab.label}
                small={hasSubTabs}
                isActive={isTabActive(tab)}
                onPress={() => handleTabPress(tab)}>
                <Tab.Label small={hasSubTabs} isActive={isTabActive(tab)}>
                  {tab.label}
                </Tab.Label>
              </Tab>
            ))}

        </Tab.Tabs>

      </ScrollView>

      <View style={{ top: hasSubTabs ? 16 : 0 }}>
        {children}
      </View>

      {hasSubTabs && (
        <SubHeader>
          <Tab.Tabs>
            {selectedTab?.subTabs
              ?.filter(subTab => checkPermissions(subTab.permissions ?? []))
              .map((subTab) => (
                <Tab
                  key={subTab.label}
                  sub
                  isActive={subTab.screenName === screenName}
                  onPress={() => goTo(subTab.screenName)}>
                  <Tab.Label isActive={subTab.screenName === screenName}>{subTab.label}</Tab.Label>
                </Tab>
              ))}
          </Tab.Tabs>
        </SubHeader>
      )}

    </Header>
  );
};

interface MainLayoutProps {
  header: React.ReactNode;
  children: React.ReactNode;
}

export const MainLayout = ({ header, children }: MainLayoutProps) => {
  return (
    <>
      <SvgAppbackground style={styles.background} />

      <MainContainer>

        {header}

        <Container>

          {children}

        </Container>

      </MainContainer>
    </>
  );
};

const styles = StyleSheet.create({
  background: {
    width: '100%',
    height: '100%',
    opacity: 1,
    position: 'absolute',
    top: 0,
    left: 0
  },
});

const SubHeader = styled.View({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  columnGap: normalize(10),
});